import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, ClipboardCheck, Milk } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useIsDesktop } from "@/hooks/useMediaQuery";

/**
 * Floating quick-log button for phones/tablets: one tap to "Record CCP reading" or
 * "Add milk reception". Only shown to users whose permissions include HACCP / milk.
 * Deep links: /app/haccp?log=1 and /app/milk?add=1 open the respective dialogs.
 */
export const QuickActions = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { canAccess, user } = useAuth();
  const [open, setOpen] = useState(false);

  if (isDesktop || !user || user.role === "client") return null;
  const actions = [
    canAccess("haccp") && { key: "ccp", label: t("quick.logCcp"), icon: ClipboardCheck, to: "/app/haccp?log=1", testid: "quick-action-ccp" },
    canAccess("milk") && { key: "milk", label: t("quick.addMilk"), icon: Milk, to: "/app/milk?add=1", testid: "quick-action-milk" },
  ].filter(Boolean);
  if (actions.length === 0) return null;

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-background/40 backdrop-blur-[2px]" onClick={() => setOpen(false)} data-testid="quick-actions-backdrop" />}
      <div className="safe-bottom fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2" data-testid="quick-actions">
        {open && actions.map((a, i) => (
          <button
            key={a.key}
            type="button"
            onClick={() => go(a.to)}
            data-testid={a.testid}
            style={{ animationDelay: `${i * 40}ms` }}
            className="flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium shadow-lg transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring animate-in fade-in slide-in-from-bottom-2"
          >
            <a.icon className="h-4 w-4 text-primary" /> {a.label}
          </button>
        ))}
        <Button
          size="icon"
          className="h-14 w-14 rounded-full shadow-xl"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={t("quick.title")}
          data-testid="quick-actions-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </Button>
      </div>
    </>
  );
};
